import { Download, Information } from '@carbon/react/icons';
import { Button, InlineNotification } from '@carbon/react';
import { Toggle } from 'components/toggle/toggle';
import { FloatingTooltip } from 'components/floating-tooltip';
import { ProgressBar } from 'components/progress-bar/progress-bar';
import { useAtom } from 'jotai';
import { subscribe } from 'lib/pusher';
import { usePolling } from 'lib/hooks/usePolling';
import request from 'lib/request';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  WellLandingAnalysisExportPusherPayload,
  WellLandingAnalysisRequestResponse,
  WellLandingAnalysisRequestStatusResponse,
} from 'types/api-responses';
import { formatMutationResource } from 'utils/misc';
import { parseUTCdateTime } from 'utils/dates';
import { format0 } from 'utils/number';
import {
  landingAnalysisCountsAtom,
  useWellLandingDefaultParametersAtom,
  wellLandingAnalysisExportAtom,
} from '../atoms';
import { WellLandingRequestsTable } from './well-landing-requests-table';

import styles from './landing-analysis-accordion.module.scss';
import { AutoUpdateModal } from './auto-update-modal';
import { DisableAutoUpdateModal } from './disable-auto-update-modal';
import { ProcessWellLandingsModal } from './process-well-landings-modal';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ActiveJobProgress {
  jobId: string;
  completionPercent: number;
  processedWells: number;
  totalWellbores: number;
}

interface QueryHrefs {
  landingAnalysisCounts: string;
  wellLandingAnalysisRequests: string;
}

interface LandingAnalysisAccordionProps {
  collectionId: string;
  collectionName: string;
  /** Polling href from collection load — present when a job is already active on page load */
  initialPollingHref?: string;
  /** Query hrefs from the collection resource object */
  queryHrefs: QueryHrefs;
  /** Export href for triggering well landing analysis export */
  exportHref?: string;
  /** Pusher channel name for export completion events */
  pusherChannel?: string;
  /** Whether landing auto update is currently enabled for this collection */
  landingAutoUpdateEnabled: boolean;
  /** Collection validity — processing is disabled when invalid */
  collectionValidity?: string;
  /** Href for fetching default landing analysis parameters */
  defaultParametersHref?: string;
}

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.statCard}>
    <span className={styles.statCardLabel}>{label}</span>
    <span className={styles.statCardValue}>{value}</span>
  </div>
);

// ─── Job Progress Bar ─────────────────────────────────────────────────────────

const JobProgressBar = ({ progress }: { progress: ActiveJobProgress }) => {
  const { jobId, completionPercent, processedWells, totalWellbores } = progress;
  const pct = Math.min(Math.max(completionPercent, 0), 100);

  return (
    <ProgressBar
      value={pct}
      statusLabel="Currently Landing Wells…"
      badge={jobId}
      helperText="Persisting results…"
      metaText={`${format0(pct)}% · ${format0(processedWells)} / ${format0(
        totalWellbores
      )} wells`}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const LandingAnalysisAccordion = ({
  collectionId,
  collectionName,
  initialPollingHref,
  queryHrefs,
  exportHref,
  pusherChannel,
  landingAutoUpdateEnabled,
  collectionValidity,
  defaultParametersHref,
}: LandingAnalysisAccordionProps) => {
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
  const [isAutoUpdateModalOpen, setIsAutoUpdateModalOpen] = useState(false);
  const [isDisableAutoUpdateModalOpen, setIsDisableAutoUpdateModalOpen] =
    useState(false);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(
    landingAutoUpdateEnabled
  );
  const [isExporting, setIsExporting] = useState(false);
  const [jobsReloadKey, setJobsReloadKey] = useState(0);
  const [exportNotification, setExportNotification] = useState<{
    kind: 'success' | 'error';
    title: string;
    subtitle?: string;
  } | null>(null);
  const [activeJobProgress, setActiveJobProgress] =
    useState<ActiveJobProgress | null>(null);
  const [jobNotification, setJobNotification] = useState<{
    kind: 'success' | 'error';
    title: string;
    subtitle: string;
  } | null>(null);

  const [pollingHref, setPollingHref] = useState<string | null>(
    initialPollingHref ?? null
  );
  const activeJobRef = useRef<{ jobId: string; totalWellbores: number } | null>(
    null
  );

  const [exportState, triggerExport] = useAtom(wellLandingAnalysisExportAtom);
  const { loading: isExportLoading } = formatMutationResource(exportState);

  // ─── Landing Parameters ──────────────────────────────────────────────────────

  const defaultParamsAtom = useWellLandingDefaultParametersAtom(
    defaultParametersHref
      ? `${defaultParametersHref}?scope=collection`
      : undefined
  );

  const [defaultParamsState] = useAtom(defaultParamsAtom);

  const landingParameters =
    defaultParamsState.data && !('error' in defaultParamsState.data)
      ? defaultParamsState.data.landingParameters
      : undefined;
  const hasParamsError =
    !defaultParamsState.loading &&
    !!defaultParamsState.data &&
    'error' in defaultParamsState.data;

  const handleDownload = useCallback(() => {
    if (!exportHref || !pusherChannel) return;

    setExportNotification(null);
    setIsExporting(true);

    const subscription = subscribe(pusherChannel);
    subscription.bind(
      'well_landing_analysis.export_complete',
      (data: WellLandingAnalysisExportPusherPayload) => {
        subscription.unbind('well_landing_analysis.export_complete');
        setIsExporting(false);
        if (data.status === 'complete' && data.download_url) {
          window.open(data.download_url, '_blank');
        } else {
          setExportNotification({
            kind: 'error',
            title: 'Export failed.',
            subtitle: data.error ?? 'Please try again.',
          });
        }
      }
    );

    triggerExport({
      url: exportHref,
      type: 'POST',
      data: null,
      onError: () => {
        subscription.unbind('well_landing_analysis.export_complete');
        setIsExporting(false);
        setExportNotification({
          kind: 'error',
          title: 'Export failed.',
          subtitle: 'Please try again.',
        });
      },
    });
  }, [exportHref, pusherChannel, triggerExport]);

  const [landingAnalysisCountsState, fetchLandingAnalysisCounts] = useAtom(
    landingAnalysisCountsAtom
  );
  const { data: landingAnalysisCounts } = formatMutationResource(
    landingAnalysisCountsState
  );

  useEffect(() => {
    fetchLandingAnalysisCounts({
      url: queryHrefs.landingAnalysisCounts,
      type: 'POST',
      data: { structure_grid_collection_id: Number(collectionId) },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, fetchLandingAnalysisCounts]);

  const handleConfirm = useCallback(
    (response: WellLandingAnalysisRequestResponse) => {
      setIsProcessModalOpen(false);

      activeJobRef.current = {
        jobId: `Job-${response.id}`,
        totalWellbores: response.totalWellbores ?? 0,
      };

      setPollingHref(response.resources.activeJobStatus?.href ?? null);
      setJobsReloadKey((k) => k + 1);
    },
    []
  );

  // ─── Polling ────────────────────────────────────────────────────────────────

  usePolling({
    fn: async () => {
      const res = (await request.get(pollingHref!)) as
        WellLandingAnalysisRequestStatusResponse | { error: string };

      if ('error' in res) throw new Error(res.error);

      const data = res;
      const isDone =
        data.status === 'processed' ||
        data.status === 'failed' ||
        data.status === 'cancelled';

      if (isDone) {
        setPollingHref(null);
        setActiveJobProgress(null);
        setJobsReloadKey((k) => k + 1);

        const processed = format0(data.progress.processed);
        const errors = data.counts.errors;
        const remaining = format0(
          data.progress.total - data.progress.processed
        );

        if (data.status === 'processed') {
          setJobNotification({
            kind: 'success',
            title: 'Finished',
            subtitle: `Successfully Completed Well Landing Processing for ${processed} Wellbores`,
          });
        } else {
          setJobNotification({
            kind: 'error',
            title: 'Finished with Errors',
            subtitle: `${processed} Wellbores persisted; ${errors} Run Errors; Job failed with ${remaining} Wellbores remaining.`,
          });
        }
      } else {
        const { total, processed } = data.progress;
        const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
        setActiveJobProgress({
          jobId: activeJobRef.current?.jobId ?? '',
          completionPercent: pct,
          processedWells: processed,
          totalWellbores: total,
        });
      }
    },
    intervalMs: 20000,
    enabled: !!pollingHref,
    immediate: true,
    onMaxRetriesReached: () => {
      setPollingHref(null);
      setActiveJobProgress(null);
    },
  });

  return (
    <div className={styles.container}>
      {jobNotification && (
        <div className={styles.notification}>
          <InlineNotification
            kind={jobNotification.kind}
            lowContrast
            title={jobNotification.title}
            subtitle={jobNotification.subtitle}
            onCloseButtonClick={() => setJobNotification(null)}
          />
        </div>
      )}
      {exportNotification && (
        <div className={styles.notification}>
          <InlineNotification
            kind={exportNotification.kind}
            lowContrast
            title={exportNotification.title}
            subtitle={exportNotification.subtitle}
            onCloseButtonClick={() => setExportNotification(null)}
          />
        </div>
      )}
      <div className={styles.actionsBar}>
        <div className={styles.toggleGroup}>
          <Toggle
            isActive={autoUpdateEnabled}
            label="Auto Update"
            ariaLabel="Auto Update"
            handleToggle={(checked: boolean) => {
              if (checked) {
                setIsAutoUpdateModalOpen(true);
              } else {
                setIsDisableAutoUpdateModalOpen(true);
              }
            }}
            disabled={collectionValidity === 'invalid'}
          />
          <FloatingTooltip
            content="Automatically runs Landing Analysis for new wells or updated Wellbore Geometry within the collection. Auto Update and Process Well Landings are disabled when the Collection is invalid."
            placement="bottom"
          >
            <Information className={styles.infoIcon} />
          </FloatingTooltip>
          <Button
            kind="tertiary"
            renderIcon={Download}
            onClick={handleDownload}
            disabled={!exportHref || isExporting || isExportLoading}
          >
            {isExporting ? 'Preparing...' : 'Download'}
          </Button>
        </div>
        <Button
          kind="primary"
          onClick={() => setIsProcessModalOpen(true)}
          disabled={!!pollingHref || collectionValidity === 'invalid'}
        >
          Process Well Landings
        </Button>
      </div>
      <div className={styles.cardsContainer}>
        <StatCard
          label="Total Jobs Run"
          value={
            landingAnalysisCounts?.totalJobsRun !== null
              ? format0(landingAnalysisCounts?.totalJobsRun)
              : '—'
          }
        />
        <StatCard
          label="Total Wells with Results"
          value={
            landingAnalysisCounts?.totalWellsWithResults !== null
              ? format0(landingAnalysisCounts?.totalWellsWithResults)
              : '—'
          }
        />
        <StatCard
          label="Wells with No Results"
          value={
            landingAnalysisCounts?.wellsWithNoResults !== null
              ? format0(landingAnalysisCounts?.wellsWithNoResults)
              : '—'
          }
        />
        <StatCard
          label="Last Run Date"
          value={
            landingAnalysisCounts?.lastRunAt
              ? parseUTCdateTime(landingAnalysisCounts.lastRunAt)
              : '—'
          }
        />
      </div>
      <ProcessWellLandingsModal
        isOpen={isProcessModalOpen}
        collectionId={collectionId}
        collectionName={collectionName}
        landingAutoUpdateEnabled={landingAutoUpdateEnabled}
        autoUpdateEnabled={autoUpdateEnabled}
        onAutoUpdateChange={setAutoUpdateEnabled}
        onClose={() => setIsProcessModalOpen(false)}
        onConfirm={handleConfirm}
        landingParameters={landingParameters}
        hasParametersError={hasParamsError}
        collectionValidity={collectionValidity}
      />
      <AutoUpdateModal
        isOpen={isAutoUpdateModalOpen}
        collectionId={collectionId}
        collectionName={collectionName}
        onClose={() => setIsAutoUpdateModalOpen(false)}
        onConfirm={() => {
          setIsAutoUpdateModalOpen(false);
          setAutoUpdateEnabled(true);
        }}
        landingParameters={landingParameters}
        hasParametersError={hasParamsError}
      />
      <DisableAutoUpdateModal
        isOpen={isDisableAutoUpdateModalOpen}
        collectionId={collectionId}
        collectionName={collectionName}
        onClose={() => setIsDisableAutoUpdateModalOpen(false)}
        onConfirm={() => {
          setIsDisableAutoUpdateModalOpen(false);
          setAutoUpdateEnabled(false);
        }}
      />
      {activeJobProgress && (
        <div className={styles.progressBarWrapper}>
          <JobProgressBar progress={activeJobProgress} />
        </div>
      )}
      <WellLandingRequestsTable
        collectionId={collectionId}
        requestsHref={queryHrefs.wellLandingAnalysisRequests}
        reloadKey={jobsReloadKey}
      />
    </div>
  );
};
